import * as _ from 'lodash-es';
import Block, { BlockEmbed } from 'quill/blots/block';
import Quill, { Delta, Parchment } from 'quill/core';
import type { Range } from 'quill/core';
import { ListContainer } from 'quill/formats/list';
import type QuillKeyboard from 'quill/modules/keyboard';
import { IS_APPLE } from '../constants/os';
import BaseKeyboard, { isTitleLine } from './BaseKeyboard';
import type { Binding, BindingObject } from './BaseKeyboard';
import { identifyLanguage } from './code/Prism';
import HeaderCollapse from './formats/HeaderCollapse';
import ListCollapse from './formats/ListCollapse';
import { getHeaderCollapse } from './useHeaderCollapse';
import { getListCollapse } from './useListCollapse';
import findQuillByBlot from './utils/findQuillByBlot';
import isSelectableInlineEmbed from './utils/isSelectableInlineEmbed';
import {
  getSiblingLine,
  isRangeACaret,
  isRangeAtLineVerticalEdge,
} from './utils/keyboardUtils';
import moveCaretIntoLineWithKeyboard from './utils/moveCaretIntoLineWithKeyboard';

type Context = Parameters<NonNullable<BindingObject['handler']>>[1];

const shiftLineWithArrows = (
  currentLine: Context['line'],
  direction: 'left' | 'right',
  revealCollapsedBlot = false,
) => {
  const quill = findQuillByBlot(currentLine);
  if (!quill) return;

  const targetLine = getSiblingLine(
    currentLine,
    direction === 'left' ? 'previous' : 'next',
  );
  if (!targetLine) return;

  const headerCollapse = getHeaderCollapse(quill);
  const isTargetLineCollapsed =
    !!headerCollapse &&
    targetLine?.domNode instanceof HTMLElement &&
    !targetLine.domNode.offsetParent;

  if (isTargetLineCollapsed) {
    if (revealCollapsedBlot) {
      headerCollapse.revealBlot(targetLine);
    } else {
      const nearestVisibleHeader = headerCollapse
        .getHeadersRelativeToBlot(
          targetLine,
          direction === 'left' ? 'above' : 'below',
        )
        .find((header) => !!header.domNode.offsetParent);

      if (nearestVisibleHeader) {
        moveCaretIntoLineWithKeyboard(nearestVisibleHeader, { direction });
      }
    }
  } else {
    moveCaretIntoLineWithKeyboard(targetLine, { direction });
  }
};

const discontinueLineFormat = (format: string): Binding => {
  return {
    key: 'Enter',
    collapsed: true,
    format: [format],
    suffix: /^$/,
    handler(range, { line, offset }) {
      if (offset < line.length() - 1) return true;
      this.quill.updateContents(
        new Delta().retain(range.index + 1).insert('\n'),
        Quill.sources.USER,
      );
      this.quill.setSelection(range.index + 1, Quill.sources.SILENT);
      return false;
    },
  };
};

const macOsSpecificBindings: Record<string, Binding> = {
  'ctrl a': {
    key: ['a', 'A'],
    collapsed: true,
    ctrlKey: true,
    shiftKey: null,
    handler(range, { event, offset }) {
      const startIndex = range.index - offset;
      this.quill.setSelection(
        {
          index: startIndex,
          length: event.shiftKey ? offset : 0,
        },
        Quill.sources.USER,
      );
    },
  },
  'ctrl e': {
    key: ['e', 'E'],
    collapsed: true,
    ctrlKey: true,
    shiftKey: null,
    handler(range, { event, line, offset }) {
      if (event.shiftKey) {
        this.quill.setSelection(
          {
            index: range.index,
            length: line.length() - offset,
          },
          Quill.sources.USER,
        );
      } else {
        const endIndex = range.index - offset + line.length() - 1;
        this.quill.setSelection(
          { index: endIndex, length: 0 },
          Quill.sources.USER,
        );
      }
    },
  },
};

const bindings: Record<string, Binding> = {
  autolink: {
    key: [' ', '\n'],
    prefix: /https?:\/\/[^\s]+$/,
    collapsed: true,
    format: { link: false, code: false },
    handler(range, context) {
      // @ts-expect-error
      const [match] = context.prefix.match(bindings.autolink?.prefix);
      const index = range.index + range.length;
      const text = context.event.key === ' ' ? ' ' : '\n';
      this.quill.insertText(index, text, Quill.sources.USER);
      this.quill.history.cutoff();
      const delta = new Delta()
        .retain(index - match.length)
        .retain(match.length, { link: match });
      this.quill.updateContents(delta, Quill.sources.USER);
      this.quill.setSelection(index + 1, Quill.sources.SILENT);
      this.quill.history.cutoff();
    },
  },
  arrow: makeCompletionHotkey('->', '→'),
  mdash: makeCompletionHotkey('--', '—', true),
  mdot: makeCompletionHotkey('...', '…'),
  'title-tab': {
    key: 'Tab',
    handler(_range, { line }) {
      if (isTitleLine(line, this.quill) && line?.next) {
        // If we are on the title row, insert a newline or advance to content
        this.quill.setSelection(
          line.next.offset(this.quill.scroll),
          Quill.sources.USER,
        );
        return false;
      }
      return true;
    },
  },
  'header-1': makeHeaderHotkey(1),
  'header-2': makeHeaderHotkey(2),
  'header-3': makeHeaderHotkey(3),
  'header-4': makeHeaderHotkey(4),
  'header-5': makeHeaderHotkey(5),
  'header-6': makeHeaderHotkey(6),
  'remove-formatting': {
    key: 48,
    shortKey: true,
    altKey: true,
    handler(range) {
      if (range.length === 0) {
        const formats = this.quill.getFormat();
        Object.keys(formats).forEach((name) => {
          this.quill.format(name, false, Quill.sources.USER);
        });
      } else {
        // @ts-expect-error
        this.quill.removeFormat(range, Quill.sources.USER);
      }
    },
  },
  'ordered-list': {
    key: 55,
    shortKey: true,
    shiftKey: true,
    handler(_range, { format: { list } }) {
      if (!this.quill.scroll.query('list')) return true;
      this.quill.format(
        'list',
        list === 'ordered' ? false : 'ordered',
        Quill.sources.USER,
      );
      return false;
    },
  },
  'bullet-list': {
    key: 56,
    shortKey: true,
    shiftKey: true,
    handler(_range, { format: { list } }) {
      if (!this.quill.scroll.query('list')) return true;
      this.quill.format(
        'list',
        list === 'bullet' ? false : 'bullet',
        Quill.sources.USER,
      );
      return false;
    },
  },
  'check-list': {
    key: 57,
    shortKey: true,
    shiftKey: true,
    handler(_range, { format: { list } }) {
      if (!this.quill.scroll.query('list')) return true;
      this.quill.format(
        'list',
        list === 'checked' || list === 'unchecked' ? false : 'unchecked',
        Quill.sources.USER,
      );
      return false;
    },
  },
  'list-space-tab': {
    key: ' ',
    collapsed: true,
    offset: 1,
    prefix: /^ $/,
    format: { list: true },
    shiftKey: null,
    handler(range) {
      this.quill.insertText(range.index, ' ', Quill.sources.USER);
      this.quill.history.cutoff();
      this.quill.deleteText(range.index - 1, 2, Quill.sources.USER);
      this.quill.formatLine(
        range.index - 1,
        // @ts-expect-error
        'indent',
        '+1',
        Quill.sources.USER,
      );
      this.quill.history.cutoff();
    },
  },
  strike: makeFormatTogglingHandler('strike', { key: 88, altKey: true }),
  'markdown-code-block': {
    key: 'Enter',
    prefix: /^```([a-z]*)$/,
    collapsed: true,
    handler(range, context, binding) {
      if (this.quill.scroll.query('code-embed')) {
        // @ts-expect-error
        const [, languageKeyword] = context.prefix.match(binding.prefix);
        const length = 3 + (languageKeyword.length as number);
        const language = identifyLanguage(languageKeyword);

        this.quill.history.cutoff();
        const delta = new Delta()
          .retain(range.index - length)
          .delete(length + 1)
          .insert({ 'code-embed': new Delta().insert('\n').ops }, { language });
        this.quill.updateContents(delta, Quill.sources.USER);
        this.quill.history.cutoff();
        this.quill.setSelection(range.index - length, Quill.sources.USER);
        return false;
      }

      return true;
    },
  },
  'markdown-header': {
    key: ' ',
    shiftKey: null,
    prefix: /^#{1,6}$/,
    format: { code: false, hint: false, blockquote: false },
    handler(range, context) {
      if (!this.quill.scroll.query('header')) return true;
      this.quill.insertText(range.index, ' ', Quill.sources.USER);
      this.quill.history.cutoff();
      const [line, offset] = this.quill.getLine(range.index + 1);
      if (!line) return true;
      const delta = new Delta()
        .retain(range.index + 1 - offset)
        .delete(context.prefix.length + 1)
        .retain(line.length() - 1 - offset)
        .retain(1, { header: Math.min(context.offset, 3), indent: null });
      this.quill.updateContents(delta, Quill.sources.USER);
      this.quill.history.cutoff();
      this.quill.setSelection(
        range.index - context.prefix.length,
        Quill.sources.SILENT,
      );
      return false;
    },
  },
  'markdown-blockquote': {
    key: ' ',
    shiftKey: null,
    prefix: /^>$/,
    format: { blockquote: false, code: false },
    handler(range, context) {
      if (!this.quill.scroll.query('blockquote')) return true;
      this.quill.insertText(range.index, ' ', Quill.sources.USER);
      this.quill.history.cutoff();
      const [line, offset] = this.quill.getLine(range.index + 1);
      if (!line) return true;
      const delta = new Delta()
        .retain(range.index + 1 - offset)
        .delete(context.prefix.length + 1)
        .retain(line.length() - 1 - offset)
        .retain(1, { blockquote: true, indent: null });
      this.quill.updateContents(delta, Quill.sources.USER);
      this.quill.history.cutoff();
      this.quill.setSelection(
        range.index - context.prefix.length,
        Quill.sources.SILENT,
      );
      return false;
    },
  },
  'markdown-hr': {
    key: 'Enter',
    prefix: /^[-*_]{3}$/,
    shiftKey: null,
    offset: 3,
    collapsed: true,
    handler(range) {
      if (!this.quill.scroll.query('hr')) {
        return true;
      }
      this.quill.insertText(range.index, '\n', Quill.sources.USER);
      this.quill.history.cutoff();
      const delta = new Delta()
        .retain(range.index - 3)
        .insert({ hr: true })
        .delete(4);
      this.quill.updateContents(delta, Quill.sources.USER);
      this.quill.history.cutoff();
      this.quill.setSelection(range.index - 2, Quill.sources.USER);
      return false;
    },
  },
  'markdown-bold-asterisk': makeBoldHotKey('*'),
  'markdown-bold-underline': makeBoldHotKey('_'),
  'markdown-italic-asterisk': makeItalicHotKey('*'),
  'markdown-italic-underline': makeItalicHotKey('_'),
  'markdown-code': {
    key: '`',
    prefix: /`[^`]*/,
    collapsed: true,
    format: { code: false },
    handler(range, context) {
      if (!this.quill.scroll.query('code')) {
        return true;
      }
      if (context.event.isComposing) {
        handleInlineMarkdown(this.quill, 'code', range, context);
      } else if (context.prefix.match(/^`+$/g) && context.format.list == null) {
        return true;
      } else if (context.prefix.endsWith('`')) {
        this.quill.deleteText(range.index - 1, 1, Quill.sources.USER);
        this.quill.format('code', !context.format.code, Quill.sources.USER);
      } else if (!context.format.code) {
        handleInlineMarkdown(this.quill, 'code', range, context);
      }
      return false;
    },
  },
  'markdown-strike': {
    key: '~',
    shiftKey: null,
    prefix: /(^|\s)~{2}[^~]+~$/,
    collapsed: true,
    format: { strike: false },
    handler(range, context) {
      handleInlineMarkdown(this.quill, 'strike', range, context);
    },
  },
  'embed enter': {
    key: ['Enter'],
    offset: 0,
    handler(range, { line }) {
      if (line instanceof BlockEmbed && range.length <= line.length()) {
        const isNextLineEmpty =
          line.next &&
          line.next.statics.blotName === Block.blotName &&
          line.next.length() <= 1;

        if (!isNextLineEmpty) {
          this.quill.insertText(range.index + 1, '\n', Quill.sources.USER);
        }
        this.quill.setSelection(range.index + 1, Quill.sources.USER);
        return false;
      }
      return true;
    },
  },
  'embed arrow-down': makeEmbedArrowHandler('ArrowDown', false),
  'embed arrow-down shift': makeEmbedArrowHandler('ArrowDown', true),
  'embed arrow-up': makeEmbedArrowHandler('ArrowUp', false),
  'embed arrow-up shift': makeEmbedArrowHandler('ArrowUp', true),
  'arrow-left at line start': {
    key: 'ArrowLeft',
    offset: 0,
    handler(range, { line, collapsed }) {
      if ((line instanceof BlockEmbed && range.length <= 1) || collapsed) {
        shiftLineWithArrows(line, 'left');
        return false;
      }
      return true;
    },
  },
  'arrow-right at line end': {
    key: 'ArrowRight',
    format: { code: false },
    handler(range, context) {
      const { line, offset } = context;
      if (!isRangeACaret(range, line)) return true;

      // Performance optimization for text.
      // This doesn't work for block embed though as `leaf` will be the first leaf in the next line,
      // which seems to be a bug on Quill side.
      if (line instanceof Block && context.suffix) return true;

      if (offset === line.length() - 1) {
        shiftLineWithArrows(line, 'right');
        return false;
      }
      return true;
    },
  },
  'arrow-left into inline embed': {
    key: ['ArrowLeft', 'Backspace'],
    collapsed: true,
    handler(range) {
      const [leaf, index] = this.quill.getLeaf(range.index);
      if (isSelectableInlineEmbed(leaf) && index === 1) {
        this.quill.setSelection(range.index - 1, 1, Quill.sources.USER);
        return false;
      }
      return true;
    },
  },
  'arrow-right into inline embed': {
    key: 'ArrowRight',
    collapsed: true,
    handler(range) {
      const [leaf, index] = this.quill.getLeaf(range.index + 1);
      if (isSelectableInlineEmbed(leaf) && index === 1) {
        this.quill.setSelection(range.index, 1, Quill.sources.USER);
        return false;
      }
      return true;
    },
  },
  'arrow-left from inline embed': {
    key: ['ArrowLeft', 'ArrowUp'],
    collapsed: false,
    handler(range) {
      if (range.length === 1) {
        const [leaf] = this.quill.getLeaf(range.index + 1);
        if (isSelectableInlineEmbed(leaf)) {
          this.quill.setSelection(range.index, 0, Quill.sources.USER);
          return false;
        }
      }
      return true;
    },
  },
  'arrow-right from inline embed': {
    key: ['ArrowRight', 'ArrowDown'],
    collapsed: false,
    handler(range) {
      if (range.length === 1) {
        const [leaf] = this.quill.getLeaf(range.index + 1);
        if (isSelectableInlineEmbed(leaf)) {
          this.quill.setSelection(range.index + 1, 0, Quill.sources.USER);
          return false;
        }
      }
      return true;
    },
  },
  'embed backspace': {
    key: ['Backspace'],
    offset: 0,
    handler(range, { empty, collapsed, line }) {
      if (line instanceof BlockEmbed && range.length <= line.length()) {
        this.quill.updateContents(
          new Delta().retain(range.index).delete(range.length),
          Quill.sources.USER,
        );
        return false;
      }

      if (line.prev && line.prev instanceof BlockEmbed && collapsed) {
        shiftLineWithArrows(line, 'left', true);

        if (empty) {
          this.quill.deleteText(range.index, 1, Quill.sources.USER);
        }
        return false;
      }
      return true;
    },
  },
  'table delete selected': {
    key: ['Backspace', 'Delete'],
    format: ['table'],
    handler() {
      const cells = Array.from(
        this.quill.root.querySelectorAll('td.ql-selected'),
      );
      if (cells.length > 0) {
        cells.forEach((node) => {
          node.parentNode?.removeChild(node);
        });
        return false;
      }
      return true;
    },
  },
  'collapsed header enter': {
    key: 'Enter',
    shiftKey: null,
    format: {
      header: true,
      [HeaderCollapse.attrName]: true,
    },
    handler(_range, { line, format }) {
      line.format(HeaderCollapse.attrName, null);
      // eslint-disable-next-line no-param-reassign
      delete format[HeaderCollapse.attrName];
      return true;
    },
  },
  'collapsed list item enter': {
    key: 'Enter',
    shiftKey: null,
    format: {
      list: true,
      [ListCollapse.attrName]: true,
    },
    handler(_range, { line, format }) {
      line.format(ListCollapse.attrName, null);
      // eslint-disable-next-line no-param-reassign
      delete format[ListCollapse.attrName];
      return false;
    },
  },
  'collapsed list item tab': {
    key: 'Tab',
    shiftKey: null,
    format: {
      list: true,
      [ListCollapse.attrName]: true,
    },
    handler(_range, { line, format }) {
      line.format(ListCollapse.attrName, null);
      // eslint-disable-next-line no-param-reassign
      delete format[ListCollapse.attrName];
      return true;
    },
  },
  'discontinue hint': discontinueLineFormat('hint'),
  'discontinue header': discontinueLineFormat('header'),
  'soft break': {
    key: 'Enter',
    shiftKey: null,
    handler(range, context) {
      if (!this.quill.scroll.query('soft')) {
        return true;
      }
      const isEnabled = !!context.format.soft;
      const { shiftKey: isShiftKey } = context.event;

      if (isEnabled && isShiftKey) return true;
      if (!isEnabled && !isShiftKey) return true;

      const lineFormats = filterFormats(
        this.quill,
        context.format,
        Parchment.Scope.BLOCK,
      );

      const allowedFormats = ['hint', 'list', 'soft'];
      if (
        Object.keys(lineFormats).length &&
        !allowedFormats.some((format) => !!lineFormats[format])
      ) {
        return true;
      }

      const attributes: { soft: boolean | null; list?: string } = {
        soft: isEnabled ? null : true,
      };

      if (lineFormats.list === 'checked') {
        attributes.list = 'unchecked';
      }

      const delta = new Delta()
        .retain(range.index)
        .delete(range.length)
        .insert('\n', lineFormats)
        .retain(context.line.length() - context.offset - 1)
        .retain(1, attributes);

      this.quill.updateContents(delta, Quill.sources.USER);
      this.quill.setSelection(range.index + 1, Quill.sources.USER);
      return false;
    },
  },
  'exit code': {
    key: 'ArrowRight',
    collapsed: true,
    format: ['code'],
    handler(_range, { line, offset }) {
      const isCursorAtEnd = offset === line.length() - 1;
      if (!isCursorAtEnd) return true;
      this.quill.format('code', false);
      return false;
    },
  },
  ...(IS_APPLE ? macOsSpecificBindings : {}),
};

function makeFormatTogglingHandler(format: string, override = {}): Binding {
  return {
    key: format[0] as string,
    shortKey: true,
    handler(_range, context) {
      if (
        'toggleContentFormat' in context.line &&
        typeof context.line.toggleContentFormat === 'function'
      ) {
        context.line.toggleContentFormat(format);
      } else {
        this.quill.format(format, !context.format[format], Quill.sources.USER);
      }
    },
    ...override,
  };
}

function makeEmbedArrowHandler(
  key: 'ArrowUp' | 'ArrowDown',
  shiftKey: boolean,
): Binding {
  return {
    key,
    shiftKey,
    handler(range, { line }) {
      if (line instanceof BlockEmbed && range.length <= line.length()) {
        if (shiftKey) {
          const length = range.length + 1;
          if (key === 'ArrowDown') {
            this.quill.setSelection(range.index, length, Quill.sources.USER);
          } else {
            this.quill.setSelection(
              range.index - 1,
              length,
              Quill.sources.USER,
            );
          }
        } else {
          shiftLineWithArrows(line, key === 'ArrowDown' ? 'right' : 'left');
        }
        return false;
      }

      const siblingLine = getSiblingLine(
        line,
        key === 'ArrowDown' ? 'next' : 'previous',
      );
      if (
        siblingLine instanceof BlockEmbed &&
        !!siblingLine.domNode.offsetParent
      ) {
        if (
          !isRangeAtLineVerticalEdge(
            range,
            line,
            key === 'ArrowDown' ? 'bottom' : 'top',
          )
        ) {
          return true;
        }

        if (shiftKey) {
          const anchorIndex = siblingLine.offset(this.quill.scroll);
          if (key === 'ArrowDown') {
            this.quill.setSelection(
              range.index,
              anchorIndex + 1 - range.index,
              Quill.sources.USER,
            );
          } else {
            this.quill.setSelection(
              anchorIndex,
              range.index - anchorIndex,
              Quill.sources.USER,
            );
          }
        } else {
          const selectionBounds = this.quill.selection.getBounds(
            key === 'ArrowDown' ? range.index + range.length : range.index,
          );
          if (selectionBounds) {
            moveCaretIntoLineWithKeyboard(siblingLine, {
              direction: key === 'ArrowDown' ? 'down' : 'up',
              xOffset: selectionBounds.left,
            });
            return false;
          }
        }
      }

      return true;
    },
  };
}

function handleInlineMarkdown(
  quill: Quill,
  format: string,
  { index }: Range,
  { event, prefix }: Context,
) {
  const trimLength = format === 'bold' || format === 'strike' ? 2 : 1;
  let code = event.key;
  if (trimLength === 2) {
    code += event.key;
  }
  const text = prefix.slice(prefix.indexOf(code));
  // Some international keyboards (ex German) will have already inserted
  if (text.endsWith(code)) {
    // Need to defer to allow compositionend to take place
    _.defer(() => {
      applyInlineMarkdown(quill, index, format, text, code);
    });
  } else {
    quill.insertText(index, event.key, Quill.sources.USER);
    applyInlineMarkdown(quill, index + 1, format, text + event.key, code);
  }
}

function applyInlineMarkdown(
  quill: Quill,
  index: number,
  format: string,
  text: string,
  code: string,
) {
  quill.history.cutoff();
  const delta = new Delta()
    .retain(index - text.length)
    .delete(code.length)
    .retain(text.length - 2 * code.length, { [format]: true })
    .delete(code.length);
  quill.updateContents(delta, Quill.sources.USER);
  quill.setSelection(index - 2 * code.length, Quill.sources.SILENT);
  quill.history.cutoff();
  quill.format(format, false);
}

function makeCompletionHotkey(
  from: string,
  to: string,
  skipLineBeginning?: boolean,
): Binding {
  const chars = _.split(from, '');
  const key = chars.pop() as string;
  const rawPrefixChars = chars.join('');
  const prefixChars = _.escapeRegExp(rawPrefixChars);
  const uniquePrefixChars = _.escapeRegExp(_.uniq(chars).join(''));
  return {
    key,
    shiftKey: null,
    prefix: new RegExp(`(^|[^${uniquePrefixChars}])${prefixChars}$`),
    format: { code: false },
    handler(range, { offset, format }) {
      if (skipLineBeginning && offset === rawPrefixChars.length) return true;
      // @ts-expect-error
      this.quill.insertText(range, key, Quill.sources.USER);
      this.quill.history.cutoff();
      const inlineFormats = filterFormats(
        this.quill,
        format,
        Parchment.Scope.INLINE,
      );
      this.quill.updateContents(
        new Delta()
          .retain(range.index - rawPrefixChars.length)
          .delete(from.length)
          .insert(to, inlineFormats),
        Quill.sources.USER,
      );
      this.quill.history.cutoff();
      this.quill.setSelection(
        range.index - (from.length - 1 - to.length),
        Quill.sources.SILENT,
      );
      return false;
    },
  };
}

function makeHeaderHotkey(level: number): Binding {
  return {
    key: level + 48, // 49 is 1
    shortKey: true,
    altKey: true,
    handler() {
      if (!this.quill.scroll.query('header')) return true;
      this.quill.format('header', Math.min(level, 3), Quill.sources.USER);
      this.quill.format('indent', null, Quill.sources.USER);
      return false;
    },
  };
}

function makeBoldHotKey(key: string): Binding {
  const escaped = _.escapeRegExp(key);
  return {
    key,
    shiftKey: null,
    prefix: new RegExp(`(^|\\s)${escaped}{2}[^${escaped}]+${escaped}$`),
    collapsed: true,
    format: { bold: false, code: false },
    handler(range, context) {
      handleInlineMarkdown(this.quill, 'bold', range, context);
    },
  };
}

function makeItalicHotKey(key: string): Binding {
  const escaped = _.escapeRegExp(key);
  return {
    key,
    shiftKey: null,
    prefix: new RegExp(`(^|\\s)${escaped}[^${escaped}]+$`),
    collapsed: true,
    format: { italic: false, code: false },
    handler(range, context) {
      handleInlineMarkdown(this.quill, 'italic', range, context);
    },
  };
}

export default class Keyboard extends BaseKeyboard {
  constructor(quill: Quill, options: QuillKeyboard['options']) {
    super(quill, {
      ...options,
      bindings: {
        ...bindings,
        ...options.bindings,
        bold: makeFormatTogglingHandler('bold'),
        italic: makeFormatTogglingHandler('italic'),
        underline: makeFormatTogglingHandler('underline'),
      },
    });
  }

  handleBackspace(range: Range, context: Context) {
    if (context.offset === 0 && context.line.prev) {
      const blot = context.line.prev;
      getHeaderCollapse(this.quill)?.revealBlot(blot);
      if (blot instanceof ListContainer && blot.children.tail) {
        getListCollapse(this.quill)?.revealBlot(blot.children.tail);
      }
    }

    super.handleBackspace(range, context);
  }
}

function filterFormats(
  quill: Quill,
  format: Context['format'],
  level: Parchment.Scope,
) {
  const commonFormats = Object.keys(format).filter(
    (name) => quill.scroll.query(name, level) && !Array.isArray(format[name]),
  );
  return _.pick(format, commonFormats);
}
